export type CapabilityFamily = 'i' | 'l' | 'c' | 'w';

interface CapabilityHandoff {
  id: string;
  family: CapabilityFamily;
  token: string;
}

interface PrivacyBoot {
  guard: 'ok';
  eligible: boolean;
  ineligible: boolean;
  pending?: boolean;
  ready?: Promise<unknown>;
  handoff: CapabilityHandoff | null;
  readToken?: (family: string) => string | null;
}

function readBoot(): PrivacyBoot | null {
  if (typeof window === 'undefined') return null;
  const boot = (window as Window & { __RDV_PRIVACY__?: PrivacyBoot }).__RDV_PRIVACY__;
  if (!boot || boot.guard !== 'ok') return null;
  return boot;
}

/** Analytics may load only after the boot guard has marked this document eligible. */
export function isDocumentTelemetryEligible(): boolean {
  const boot = readBoot();
  return Boolean(boot && boot.eligible === true && boot.ineligible !== true);
}

/** True while the auth-backup read has not finished. Sign-in treats that as closed. */
export function isDocumentTelemetryPending(): boolean {
  const boot = readBoot();
  return Boolean(boot && boot.pending === true);
}

/**
 * Resolves when the boot guard has finished the auth-backup read.
 * A missing promise means this document has no boot guard, so callers decide immediately.
 * A rejected read still resolves: the guard fails closed and leaves the document ineligible.
 */
export function whenDocumentTelemetryDecided(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  try {
    const ready = readBoot()?.ready;
    if (ready && typeof ready.then === 'function') {
      return Promise.resolve(ready).then(() => undefined, () => undefined);
    }
  } catch {
    return Promise.resolve();
  }
  return Promise.resolve();
}

export function readCapabilityToken(family: CapabilityFamily): string | null {
  const boot = readBoot();
  if (!boot || typeof boot.readToken !== 'function') return null;
  const token = boot.readToken(family);
  return typeof token === 'string' && token.length > 0 ? token : null;
}

export function capabilityToken(family: CapabilityFamily, routeToken: string | undefined): string | undefined {
  if (routeToken && routeToken.length > 0) return routeToken;
  return readCapabilityToken(family) ?? undefined;
}

export function canonicalCapabilityUrl(family: CapabilityFamily, token: string): string {
  const url = new URL(`${window.location.origin}/${family}/${encodeURIComponent(token)}`);
  return url.toString();
}

export function assignCapabilityDocument(path: string): void {
  window.location.assign(new URL(path, window.location.href).href);
}

export function replaceCapabilityDocument(path: string): void {
  window.location.replace(new URL(path, window.location.href).href);
}
